import React from 'react'
import './OrderStatusTracker.css'

const OrderStatusTracker = ({ order }) => {
    if (!order) {
        return null
    }

    // Check if payment is completed
    const isPaymentCompleted = (order.payment?.paidAt !== null && order.payment?.paidAt !== undefined) || order.status === 'PAID'

    // Check if all variants in all dishes are completed
    const isAllVariantsCompleted = () => {
        if (!order.dishes || order.dishes.length === 0) return false

        for (const dish of order.dishes) {
            if (!dish.variants || dish.variants.length === 0) return false

            for (const variant of dish.variants) {
                if (!variant.completedAt) return false
            }
        }

        return true
    }

    const allVariantsCompleted = isAllVariantsCompleted()

    // Define status steps with their completion conditions
    const statusSteps = [
        {
            key: 'payment',
            label: 'Waiting for payment',
            isCompleted: isPaymentCompleted,
        },
        {
            key: 'preparing',
            label: 'Order sent to kitchen',
            isCompleted: allVariantsCompleted, // Preparing is completed when all variants are ready
            isActive: isPaymentCompleted && !allVariantsCompleted,
        },
        {
            key: 'completed',
            label: 'All Completed',
            isCompleted: allVariantsCompleted,
        },
        // Future use: Completed step
        // {
        //     key: 'completed',
        //     label: 'Completed',
        //     isCompleted: allVariantsCompleted && order.status === 'PAID',
        // },
    ]

    // Determine current step status
    const getStepStatus = (step, index) => {
        // If step is completed, show as completed
        if (step.isCompleted) {
            return 'completed'
        }

        // Check if this is the active step (for Preparing)
        if (step.isActive) {
            return 'active'
        }

        // Check if all previous steps are completed
        const previousStepsCompleted = statusSteps.slice(0, index).every(s => s.isCompleted)

        // If previous steps are completed, this is the current active step
        if (previousStepsCompleted) {
            return 'active'
        }

        // Otherwise, step is inactive
        return 'inactive'
    }

    // Get icon for each step based on status
    const getStepIcon = (step, stepStatus) => {
        if (stepStatus === 'completed') {
            return <i className="bx bx-check"></i>
        }
        if (stepStatus === 'active') {
            // Different icons for different active steps
            switch (step.key) {
                case 'payment':
                    return <i className="bx bx-credit-card"></i>
                case 'preparing':
                    return <i className="bx bx-time"></i> // Timer icon without rotation
                case 'ready':
                    return <i className="bx bx-package"></i>
                // Future use: Completed step icon
                // case 'completed':
                //     return <i className="bx bx-check-circle"></i>
                default:
                    return null
            }
        }
        // Inactive state - show empty circle
        return null
    }

    return (
        <div className="order-status-tracker">
            <div className="status-steps">
                {statusSteps.map((step, index) => {
                    const stepStatus = getStepStatus(step, index)
                    const icon = getStepIcon(step, stepStatus)
                    return (
                        <div key={step.key} className={`status-step status-step-${stepStatus}`}>
                            <div className="status-indicator">
                                {icon}
                                {!icon && stepStatus === 'active' && <div className="status-indicator-pulse"></div>}
                            </div>
                            <span className="status-label">{step.label}</span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default OrderStatusTracker
